import * as React from "react";
import {
  Box,
  Breadcrumbs,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Pagination,
  Button,
  Link,
} from "@mui/material";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CodeIcon from "@mui/icons-material/Code";
import Footer from "../components/Footer";
import JsonDataModal from "../components/admin/JsonDataModal";
import Navigation from "../components/Navigation";
import SquarespacePageHeader from "../components/SquarespacePageHeader";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";

export default function SquarespaceSubscriptions() {
  const api = useApi();
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [pagination, setPagination] = React.useState({
    total: 0,
    limit: 20,
    offset: 0,
    page: 1,
  });
  const [jsonModalOpen, setJsonModalOpen] = React.useState(false);

  React.useEffect(() => {
    const fetchSubscriptions = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          limit: pagination.limit.toString(),
          offset: pagination.offset.toString(),
        });
        const responseData = await api.get(`/admin/squarespace/subscriptions?${params}`);
        const items = responseData.data || [];
        const paginationData = responseData.pagination || {};

        setSubscriptions(items);
        setPagination((prev) => ({
          ...prev,
          total: paginationData.total || items.length,
          page: Math.floor((paginationData.offset || 0) / prev.limit) + 1,
        }));
      } catch (err) {
        console.error("Error fetching subscriptions:", err);
        setError(err.message || "Failed to fetch subscriptions");
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
  }, [api, pagination.limit, pagination.offset]);

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({
      ...prev,
      page: newPage,
      offset: (newPage - 1) * prev.limit,
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    const s = (status || "").toUpperCase();
    switch (s) {
      case "ACTIVE":
        return "success";
      case "PAUSED":
        return "warning";
      case "CANCELLED":
      case "EXPIRED":
        return "error";
      default:
        return "default";
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={RouterLink}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={RouterLink}
                  to="/dashboard/squarespace"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Squarespace
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Subscriptions
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title="Squarespace Subscriptions"
              description="Manage Squarespace subscriptions"
              icon={<SubscriptionsIcon sx={{ color: "primary.main" }} />}
            >
              {!loading && (
                <Button
                  variant="outlined"
                  startIcon={<CodeIcon />}
                  onClick={() => setJsonModalOpen(true)}
                >
                  JSON
                </Button>
              )}
            </SquarespacePageHeader>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Customer ID</TableCell>
                        <TableCell>Frequency</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Expires</TableCell>
                        <TableCell>Auto Renew</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subscriptions.map((subscription) => (
                        <TableRow key={subscription.id} hover>
                          <TableCell>{subscription.productName || "—"}</TableCell>
                          <TableCell>{subscription.productType || "—"}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                            {subscription.customerId ? (
                              <Link
                                component={RouterLink}
                                to={`/dashboard/squarespace/customers/${subscription.customerId}`}
                              >
                                {subscription.customerId}
                              </Link>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell>{subscription.frequency || "—"}</TableCell>
                          <TableCell>
                            <Chip
                              label={(subscription.resoldStatus || "—")
                                .charAt(0)
                                .toUpperCase() +
                                (subscription.resoldStatus || "")
                                .slice(1)
                                .toLowerCase()}
                              color={getStatusColor(subscription.resoldStatus)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatDate(subscription.expiresOn)}</TableCell>
                          <TableCell>{subscription.autoRenew ? "Yes" : "No"}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<VisibilityIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/dashboard/squarespace/subscriptions/${subscription.id}`);
                              }}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {totalPages > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={pagination.page}
                      onChange={handlePageChange}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>
      <JsonDataModal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        data={{
          subscriptions,
          total: subscriptions.length,
          pagination,
        }}
        title="Subscriptions JSON"
      />
    </>
  );
}
